import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useApi, configApiRef } from '@backstage/frontend-plugin-api';
import LogoIcon from './LogoIcon';
import { TegelFooter } from './TegelFooter';
import { TegelHeader } from './TegelHeader';
import { TdsIcon } from '@scania/tegel-react';
import {
  appRoutePaths,
  catalogEntitiesRoutePath,
  catalogIntroRoutePath,
  sidebarItems,
  type SidebarItemConfig,
  type TdsIconName,
  utilitySidebarItems,
} from './navigation';
import {
  TegelSidebarStateProvider,
  useTegelSidebarState,
} from './SidebarState';
import {
  installRecentVisitTracker,
  recordRecentVisit,
} from '../home/recentVisits';
import { UsageAnalyticsRouteTracker } from '../usageAnalytics/UsageAnalyticsRouteTracker';
import { TegelButtonBase } from '../common/Tegel';

// Environment Banner Component - shows DEV, nothing in production.
const EnvironmentBanner = () => {
  const config = useApi(configApiRef);

  // Detect environment from config (set via ENVIRONMENT env var at runtime)
  const environment = useMemo(() => {
    const env = config.getOptionalString('app.environment') || 'production';
    // Normalize 'dev' and 'development' to 'development' for consistent handling
    return env === 'dev' ? 'development' : env;
  }, [config]);

  // Don't show banner in production
  if (environment === 'production' || environment === 'prod') {
    return null;
  }

  // Determine banner color and text based on environment
  const bannerConfig = {
    development: {
      color: '#FF6B00', // Scania orange
      text: 'DEV ENVIRONMENT',
    },
  }[environment];

  if (!bannerConfig) {
    return null;
  }

  return (
    <div
      className="tegel-environment-banner"
      style={{ backgroundColor: bannerConfig.color }}
    >
      {bannerConfig.text}
    </div>
  );
};

const createSidebarIcon = (name: TdsIconName, title: string) => {
  const SidebarIcon = ({ className }: { className?: string }) => (
    <span
      aria-hidden="true"
      className={['tegel-sidebar-icon', className].filter(Boolean).join(' ')}
    >
      <TdsIcon name={name} size="20px" svgTitle={title} />
    </span>
  );
  return SidebarIcon;
};

const catalogSearchHasEntityState = (search: string) => {
  const params = new URLSearchParams(search);

  return Array.from(params.keys()).some(
    key =>
      key === 'kind' ||
      key.startsWith('filters[') ||
      key === 'limit' ||
      key === 'offset' ||
      key === 'cursor',
  );
};

const isCatalogRoute = (pathname: string) =>
  pathname === appRoutePaths.catalog ||
  pathname.startsWith(`${appRoutePaths.catalog}/`);

const routeFromTo = (to: string) => new URL(to, 'https://driveline.local');

const isCatalogSubItemActive = (
  to: string,
  location: ReturnType<typeof useLocation>,
) => {
  if (!isCatalogRoute(location.pathname)) {
    return false;
  }

  const view = new URLSearchParams(location.search).get('view');
  const childRoute = routeFromTo(to);
  const childView = childRoute.searchParams.get('view');
  const isCatalogIntroView =
    location.pathname === appRoutePaths.catalog &&
    (view === null || view === 'getting-started' || view === 'introduction') &&
    !catalogSearchHasEntityState(location.search);

  if (childView === 'getting-started') {
    if (!isCatalogIntroView) {
      return false;
    }

    if (childRoute.hash) {
      return (
        location.hash === childRoute.hash ||
        (to === catalogIntroRoutePath && !location.hash)
      );
    }

    return !location.hash;
  }

  if (to === catalogEntitiesRoutePath) {
    return (
      location.pathname.startsWith(`${appRoutePaths.catalog}/`) ||
      view === 'entities' ||
      catalogSearchHasEntityState(location.search)
    );
  }

  return false;
};

const isSidebarSubItemActive = (
  item: SidebarItemConfig,
  child: NonNullable<SidebarItemConfig['children']>[number],
  childIndex: number,
  location: ReturnType<typeof useLocation>,
) => {
  if (item.to === appRoutePaths.catalog) {
    return isCatalogSubItemActive(child.to, location);
  }

  const childRoute = routeFromTo(child.to);

  if (location.pathname !== childRoute.pathname) {
    return false;
  }

  const childView = childRoute.searchParams.get('view');
  const isDefaultChild = item.defaultTo === child.to;

  if (childView) {
    const currentView = new URLSearchParams(location.search).get('view');

    if (!currentView && isDefaultChild) {
      return true;
    }

    if (currentView !== childView) {
      return false;
    }

    if (childRoute.hash) {
      return (
        location.hash === childRoute.hash ||
        (childIndex === 0 && !location.hash)
      );
    }

    return !location.hash;
  }

  if (childRoute.hash) {
    return (
      location.hash === childRoute.hash || (childIndex === 0 && !location.hash)
    );
  }

  return childIndex === 0 && !location.hash;
};

const SidebarLogo = () => {
  const { isPinned } = useTegelSidebarState();

  return (
    <div className="tegel-sidebar-logo">
      <Link
        to={appRoutePaths.home}
        className={[
          'tegel-sidebar-logo-link',
          !isPinned ? 'is-closed' : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label="Driveline home"
      >
        {isPinned ? (
          <span className="tegel-sidebar-app-name">Driveline</span>
        ) : (
          <LogoIcon />
        )}
      </Link>
    </div>
  );
};

const TegelSidebarItem = ({ item }: { item: SidebarItemConfig }) => {
  const { isPinned } = useTegelSidebarState();
  const location = useLocation();
  const Icon = createSidebarIcon(item.icon, item.label);
  const isGroupActive =
    item.children?.some((child, index) =>
      isSidebarSubItemActive(item, child, index, location),
    ) ?? false;
  const [isExpanded, setIsExpanded] = useState(isGroupActive);

  useEffect(() => {
    if (isGroupActive) {
      setIsExpanded(true);
    }
  }, [isGroupActive]);

  const className = ({ isActive }: { isActive: boolean }) =>
    [
      'tegel-side-menu-item',
      isActive ? 'is-active' : undefined,
      !isPinned ? 'is-closed' : undefined,
    ]
      .filter(Boolean)
      .join(' ');

  const groupParentClassName = [
    'tegel-side-menu-item',
    'tegel-side-menu-button',
    'tegel-side-menu-group-button',
    isGroupActive ? 'is-active' : undefined,
    isExpanded ? 'is-expanded' : undefined,
    !isPinned ? 'is-closed' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <Icon />
      <span className="tegel-side-menu-label">{item.label}</span>
    </>
  );

  if (item.children?.length) {
    if (!isPinned) {
      return (
        <NavLink
          aria-label={item.label}
          className={className}
          title={item.label}
          to={item.defaultTo ?? item.to}
        >
          {content}
        </NavLink>
      );
    }

    return (
      <div className="tegel-side-menu-group">
        <TegelButtonBase
          aria-expanded={isExpanded}
          className={groupParentClassName}
          onClick={() => setIsExpanded(current => !current)}
          title={item.label}
        >
          {content}
          <span className="tegel-side-menu-expander" aria-hidden="true">
            <TdsIcon
              name={isExpanded ? 'chevron_up' : 'chevron_down'}
              size="16px"
              svgTitle={isExpanded ? 'Collapse' : 'Expand'}
            />
          </span>
        </TegelButtonBase>

        {isExpanded && (
          <div className="tegel-side-menu-sublist">
            {item.children.map((child, index) => (
              <Link
                key={child.to}
                to={child.to}
                className={[
                  'tegel-side-menu-subitem',
                  isSidebarSubItemActive(item, child, index, location)
                    ? 'is-active'
                    : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={
                  isSidebarSubItemActive(item, child, index, location)
                    ? 'page'
                    : undefined
                }
              >
                {child.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (item.external) {
    return (
      <a
        aria-label={item.label}
        className={['tegel-side-menu-item', !isPinned ? 'is-closed' : undefined]
          .filter(Boolean)
          .join(' ')}
        href={item.to}
        rel="noopener noreferrer"
        target="_blank"
        title={item.label}
      >
        {content}
      </a>
    );
  }

  return (
    <NavLink
      aria-label={item.label}
      className={className}
      end={item.end}
      title={item.label}
      to={item.to}
    >
      {content}
    </NavLink>
  );
};

const TegelSidebar = () => {
  const { isPinned } = useTegelSidebarState();

  return (
    <aside
      aria-label="Primary navigation"
      className={[
        'tegel-sidebar-shell',
        isPinned ? 'is-open' : 'is-closed',
      ].join(' ')}
    >
      <nav className="tegel-side-menu">
        <SidebarLogo />
        <div className="tegel-side-menu-scroll">
          <div className="tegel-side-menu-list">
            {sidebarItems.map(item => (
              <TegelSidebarItem item={item} key={item.to} />
            ))}
          </div>
          <div className="tegel-side-menu-spacer" />
          <div className="tegel-side-menu-list">
            {utilitySidebarItems.map(item => (
              <TegelSidebarItem item={item} key={item.to} />
            ))}
          </div>
        </div>
      </nav>
    </aside>
  );
};

const AppShell = ({ children }: PropsWithChildren<{}>) => {
  const { isPinned } = useTegelSidebarState();
  const location = useLocation();
  const docsRouteSegments = location.pathname
    .slice(appRoutePaths.docs.length)
    .split('/')
    .filter(Boolean);
  const isTechDocsReaderRoute =
    location.pathname.startsWith(`${appRoutePaths.docs}/`) &&
    docsRouteSegments.length >= 3;

  return (
    <div
      className={[
        'tegel-app-shell',
        isPinned ? 'is-sidebar-open' : 'is-sidebar-closed',
      ].join(' ')}
    >
      <TegelHeader />
      <div
        className={[
          'tegel-app-content',
          isTechDocsReaderRoute ? 'tegel-techdocs-reader-route' : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
      <TegelFooter />
    </div>
  );
};

const RecentVisitTracker = () => {
  const location = useLocation();

  useEffect(() => installRecentVisitTracker(), []);

  useEffect(() => {
    recordRecentVisit(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

const viewAnchorTargets: Record<string, string> = {
  apis: 'api-entities',
  documentation: 'documentation',
  groups: 'my-groups',
  import: 'import-component',
  services: 'available-services',
};

const ViewAnchorScroller = () => {
  const location = useLocation();

  useEffect(() => {
    const hashTargetId = location.hash
      ? decodeURIComponent(location.hash.slice(1))
      : undefined;
    const view = new URLSearchParams(location.search).get('view');
    const shouldScrollToTop =
      !hashTargetId && (view === 'getting-started' || view === 'introduction');

    if (shouldScrollToTop) {
      const frame = window.requestAnimationFrame(() => {
        window.scrollTo({ left: 0, top: 0 });
      });

      return () => window.cancelAnimationFrame(frame);
    }

    if (!hashTargetId && !view) {
      return undefined;
    }

    const targetId = hashTargetId ?? viewAnchorTargets[view ?? ''];

    if (!targetId) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ block: 'start' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.hash, location.pathname, location.search]);

  return null;
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  return (
    <TegelSidebarStateProvider>
      <RecentVisitTracker />
      <ViewAnchorScroller />
      <UsageAnalyticsRouteTracker />
      <EnvironmentBanner />
      <TegelSidebar />
      <AppShell>{children}</AppShell>
    </TegelSidebarStateProvider>
  );
};
